// src/App.js
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import useSessionGuard from './hooks/useSessionGuard';
import { isAuthSessionValid } from './utils/authSession';

import Dashboard from './pages/Dashboard';
import CategoryPage from './pages/category/Category';
import Profile from './pages/Profile';
import OnlineStore from './pages/OnlineStore';
import ProductGrid from './pages/products/ProductGrid';
import ProductList from './pages/products/Products';
import ProductDetailPage from './pages/products/ProductDetails';
import AddProduct from './pages/products/AddProduct';
import ReceivedOrder from './pages/products/ReceivedOrder';
import Customers from './pages/customers/Customers';
import Orders from './pages/orders/Orders';
import OrderCartPage from './pages/orders/OrderCart';
import NoOrdersYetPage from './pages/orders/NoOrder';
import ReturnItemsList from './pages/orders/ReturnItems';
import PurchaseOrder from './pages/orders/PurchaseOrder';
import OrderDetails from './pages/orders/OrderDetails';
import EditOrder from './pages/orders/EditOrder';
import ReturnItems from './pages/orders/ReturnItems';
import Warehouse from './pages/inventory/Warhouse';
import AllPurchaseItems from './pages/inventory/AllPurchaseItems';
import InvoiceDetails from './pages/invoice/InvoiceDetails';
import InvoiceList from './pages/invoice/InvoiceList';
import LoginPage from './internalpages/signin';
import ProtectedRoute from './components/ProtectedRoute';
import EditProductForm from './pages/products/EditProduct';
import CustomerReview from './pages/CustomerReview';
import Analytics from './pages/Analytics';
import AffiliatesList from './pages/affiliates/AffiliatesList';
import AddAffiliate from './pages/affiliates/AddAffiliate';
import AffiliateDetails from './pages/affiliates/AffiliateDetails';
import RequireSuperAdmin from './components/RequireSuperAdmin';
import AdminUsers from './pages/super/AdminUsers';
import AdminUserForm from './pages/super/AdminUserForm';
import AnnouncementHeadings from './pages/super/AnnouncementHeadings';
import ShopAccounts from './pages/super/ShopAccounts';
import ShopAccountForm from './pages/super/ShopAccountForm';
import ShopDetail from './pages/super/ShopDetail';
import WebsiteBanner from './pages/WebsiteBanner';
import DeliverySettings from './pages/DeliverySettings';
import DealsList from './pages/deals/DealsList';
import DealForm from './pages/deals/DealForm';

function App() {
  useSessionGuard();

  const isLoggedIn = isAuthSessionValid();

  return (
    <Routes>
      {/* Public route for customer reviews - no login required */}
      <Route path="/review" element={<CustomerReview />} />
      
      {!isLoggedIn && (
        <>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<LoginPage />} />
        </>
      )}

      <Route path="/" element={<Layout />}>
        <Route element={<ProtectedRoute />}>
          <Route path="/login" element={<Navigate to="/" />} />
          <Route index element={<Dashboard />} /> {/* Dashboard shown at "/" */}

          {/* Top-Level Pages */}
          <Route path="category" element={<CategoryPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="online-store" element={<OnlineStore />} />
          <Route path="products/grid" element={<ProductGrid />} />
          <Route path="products/list" element={<ProductList />} />
         <Route path="products/details/:productId" element={<ProductDetailPage />} />
          <Route path="products/add" element={<AddProduct />} />
          <Route path="products/edit/:productId" element={<EditProductForm />} />
          <Route path="products/review" element={<h2>Product Review Page</h2>} />
          <Route path="inventory/received-order" element={<ReceivedOrder />} />
          <Route path="customer/list" element={<Customers />} />
          <Route path="orders/list" element={<Orders />} />
          <Route path="orders/cart" element={<OrderCartPage />} />
          <Route path="orders/no-order" element={<NoOrdersYetPage />} />
          <Route path="orders/return-list" element={<ReturnItemsList />} />
          <Route path="orders/purchase-order" element={<PurchaseOrder />} />
          <Route path="orders/details/:id" element={<OrderDetails />} />
          <Route path="edit-order/:id" element={<EditOrder />} />
          <Route path="inventory/return-items" element={<ReturnItems />} />
          <Route path="affiliates/list" element={<AffiliatesList />} />
          <Route path="affiliates/add" element={<AddAffiliate />} />
          <Route path="affiliates/details/:id" element={<AffiliateDetails />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="website-banner" element={<WebsiteBanner />} />
          <Route path="delivery-settings" element={<DeliverySettings />} />
          <Route path="deals/list" element={<DealsList />} />
          <Route path="deals/add" element={<DealForm />} />
          <Route path="deals/edit/:id" element={<DealForm />} />
          <Route path="invoice" element={<InvoiceList />} />
          <Route path="invoice/create/:id" element={<InvoiceDetails />} />
          <Route path="inventory/list" element={<Warehouse />} />
          <Route path="inventory/all-purchase-items" element={<AllPurchaseItems />} />

          <Route element={<RequireSuperAdmin />}>
            <Route path="super" element={<Navigate to="/super/admins" replace />} />
            <Route path="super/admins" element={<AdminUsers />} />
            <Route path="super/admins/add" element={<AdminUserForm />} />
            <Route path="super/admins/edit/:id" element={<AdminUserForm />} />
            <Route path="super/announcements" element={<AnnouncementHeadings />} />
            <Route path="super/shops" element={<ShopAccounts />} />
            <Route path="super/shops/view/:id" element={<ShopDetail />} />
            <Route path="super/shops/add" element={<ShopAccountForm />} />
            <Route path="super/shops/edit/:id" element={<ShopAccountForm />} />
          </Route>

          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
